import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import {
  fetchWorkflows,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  type Workflow,
  type WorkflowInput,
  type WorkflowLeadForm,
} from "../services/workflowsService";
import { fetchLeads, type Lead } from "../services/leadsService";
import { isLeadCallable, leadPhone } from "../services/leadPhone";
import { fetchAgents, type Agent } from "../services/agentsService";
import { placeAiCall } from "../services/callsService";
import { announceCallProblem } from "../services/callFeedback";

import { DIALOG_COLORS, Switch as UiSwitch } from "../../../components/ui";
// ===========================================================================
// Workflows — LIVE section (CRM automations). Today the only wired automation
// is: lead.created → ai_call. Creating/enabling a workflow makes the backend
// place an AI qualification call whenever a lead is created. From here you can
// also fire a test call against an existing lead.
// ===========================================================================

const emptyForm: WorkflowInput = {
  name: "",
  description: "",
  trigger: "lead.created",
  action: "ai_call",
  enabled: true,
  config: { crmAgentId: null, agentId: null, agentPhoneNumberId: null, fromLabel: null, objective: null, onlyWhenPhonePresent: true, leadForm: "any" },
};

// A qué leads aplica un workflow. Un lead de demo o de prueba piloto corre el
// workflow de su formulario; si no hay uno, corre el de «Cualquier lead».
const LEAD_FORM_OPTIONS: { value: WorkflowLeadForm; label: string; hint: string }[] = [
  {
    value: "any",
    label: "Cualquier lead",
    hint: "Corre para los leads que no tienen un workflow propio (API, anuncios y los formularios sin workflow).",
  },
  {
    value: "demo",
    label: "Solicitud de demo",
    hint: "Solo quien llenó el formulario de contacto o la agenda. La llamada confirma sus datos de contacto y la demo.",
  },
  {
    value: "pilot",
    label: "Prueba piloto",
    hint: "Solo quien registró una prueba piloto. La llamada confirma los datos obligatorios de ese registro.",
  },
];

function leadFormLabel(value?: WorkflowLeadForm | null) {
  return LEAD_FORM_OPTIONS.find((o) => o.value === value)?.label ?? LEAD_FORM_OPTIONS[0].label;
}

export default function WorkflowsLive() {
  const tc = buildThemeClasses();

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Workflow | null>(null);
  const [creating, setCreating] = useState(false);
  const [picker, setPicker] = useState<Workflow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await fetchWorkflows();
      setWorkflows(page.data);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudieron cargar los workflows");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (wf: Workflow) => {
    setBusyId(wf.id);
    setWorkflows((prev) => prev.map((w) => (w.id === wf.id ? { ...w, enabled: !w.enabled } : w)));
    try {
      await updateWorkflow(wf.id, { enabled: !wf.enabled });
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar", confirmButtonColor: brand.primary });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (wf: Workflow) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar workflow",
      text: `¿Eliminar "${wf.name}"?`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    setWorkflows((prev) => prev.filter((w) => w.id !== wf.id));
    try {
      await deleteWorkflow(wf.id);
    } catch {
      await load();
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-500/15 text-teal-500">
              <i className="ri-flow-chart text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Workflows</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Automatizaciones del CRM · Lead creado → llamada de IA para calificar
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.text}`}>
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400"
            >
              <i className="ri-add-line" /> Nuevo workflow
            </button>
          </div>
        </div>

        {loading ? (
          <div className={`rounded-2xl border p-10 text-center text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
            <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando...
          </div>
        ) : error ? (
          <div className={`rounded-2xl border p-10 text-center ${tc.cardBorder} ${tc.cardBg}`}>
            <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
            <p className="text-sm text-red-500">{error}</p>
            <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">Reintentar</button>
          </div>
        ) : workflows.length === 0 ? (
          <div className={`rounded-2xl border p-10 text-center ${tc.cardBorder} ${tc.cardBg}`}>
            <i className="ri-flow-chart mb-2 block text-3xl text-teal-500" />
            <p className={`text-sm ${tc.textSecondary}`}>
              Aún no hay workflows. Crea uno para que cada lead nuevo reciba una llamada de IA automáticamente.
            </p>
            <button onClick={() => setCreating(true)} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
              Crear el primero
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {workflows.map((wf) => (
              <div key={wf.id} className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div>
                    <h3 className={`font-semibold ${tc.text}`}>{wf.name}</h3>
                    {wf.description && <p className={`text-xs ${tc.textSecondary}`}>{wf.description}</p>}
                  </div>
                  <Switch on={wf.enabled} busy={busyId === wf.id} onClick={() => toggle(wf)} />
                </div>

                <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-sky-500">
                    <i className="ri-flashlight-line mr-1" />Lead creado
                  </span>
                  <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-500">
                    <i className="ri-filter-3-line mr-1" />{leadFormLabel(wf.config?.leadForm)}
                  </span>
                  <i className={`ri-arrow-right-line ${tc.textSecondary}`} />
                  <span className="rounded-full bg-teal-500/15 px-2 py-0.5 text-teal-500">
                    <i className="ri-phone-line mr-1" />Llamada IA
                  </span>
                </div>

                <div className="mb-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <Stat label="Disparadas" value={wf.stats?.triggered ?? 0} tc={tc} />
                  <Stat label="Completadas" value={wf.stats?.completed ?? 0} tc={tc} />
                  <Stat label="Errores" value={wf.stats?.failed ?? 0} tc={tc} />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => setPicker(wf)} className="rounded-lg border border-teal-500 px-3 py-1.5 text-xs text-teal-600 hover:bg-teal-500/10">
                    <i className="ri-play-line mr-1" />Probar con lead
                  </button>
                  <button onClick={() => setEditing(wf)} className="rounded-lg border px-3 py-1.5 text-xs hover:bg-primary-500/10">
                    <i className="ri-edit-line mr-1" />Editar
                  </button>
                  <button onClick={() => remove(wf)} className="rounded-lg border border-red-300 px-3 py-1.5 text-xs text-red-500 hover:bg-red-500/10">
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className={`mt-4 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Las grabaciones y transcripciones de cada llamada aparecen en <strong>Tareas → Llamadas</strong>.
        </p>
      </div>

      {(creating || editing) && (
        <WorkflowFormModal
          tc={tc}
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await load();
          }}
        />
      )}

      {picker && (
        <LeadPickerModal tc={tc} workflow={picker} onClose={() => setPicker(null)} />
      )}
    </div>
  );
}

// --- form modal ------------------------------------------------------------
function WorkflowFormModal({
  tc,
  initial,
  onClose,
  onSaved,
}: {
  tc: ReturnType<typeof buildThemeClasses>;
  initial: Workflow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<WorkflowInput>(
    initial
      ? {
          name: initial.name,
          description: initial.description ?? "",
          enabled: initial.enabled,
          trigger: initial.trigger,
          action: initial.action,
          config: { ...initial.config },
        }
      : { ...emptyForm },
  );
  const [saving, setSaving] = useState(false);
  const [aiAgents, setAiAgents] = useState<Agent[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const page = await fetchAgents({ type: "ai", status: "active", limit: 200 });
        setAiAgents(page.data);
      } catch {
        setAiAgents([]);
      }
    })();
  }, []);

  const setCfg = (k: string, v: string) =>
    setForm((f) => ({ ...f, config: { ...f.config, [k]: v || null } }));

  const save = async () => {
    if (!form.name.trim()) {
      Swal.fire({ icon: "warning", title: "Falta el nombre", confirmButtonColor: brand.primary });
      return;
    }
    setSaving(true);
    try {
      if (initial) await updateWorkflow(initial.id, form);
      else await createWorkflow(form);
      onSaved();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || err.message, confirmButtonColor: brand.primary });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.cardBg} ${tc.text}`;

  return (
    <Modal
      title={initial ? "Editar workflow" : "Nuevo workflow"}
      onClose={onClose}
      themeClasses={tc}
      content={
        <div className="space-y-3">
          <Field label="Nombre" tc={tc}>
            <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Llamada de bienvenida a leads" />
          </Field>
          <Field label="Descripción" tc={tc}>
            <input className={inputCls} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Llama y califica cada lead nuevo" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Disparador" tc={tc}>
              <input className={`${inputCls} opacity-70`} value="Lead creado" disabled />
            </Field>
            <Field label="Acción" tc={tc}>
              <input className={`${inputCls} opacity-70`} value="Llamada de IA" disabled />
            </Field>
          </div>

          <Field label="Aplica a" tc={tc}>
            <select
              className={inputCls}
              value={form.config?.leadForm ?? "any"}
              onChange={(e) => setCfg("leadForm", e.target.value)}
            >
              {LEAD_FORM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className={`mt-1 text-xs ${tc.textSecondary}`}>
              {LEAD_FORM_OPTIONS.find((o) => o.value === (form.config?.leadForm ?? "any"))?.hint}
            </p>
          </Field>

          <div className={`rounded-xl border p-3 ${tc.border}`}>
            <Field label="Agente IA que atiende la llamada" tc={tc}>
              <select
                className={inputCls}
                value={form.config?.crmAgentId ?? ""}
                onChange={(e) => setCfg("crmAgentId", e.target.value)}
              >
                <option value="">— Agente por defecto del servidor —</option>
                {aiAgents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.useCase && a.useCase !== "custom" ? ` · ${a.useCase}` : ""}
                  </option>
                ))}
              </select>
            </Field>
            <p className={`mb-2 mt-1 text-[11px] ${tc.textSecondary}`}>
              El guion, las preguntas y los datos a recopilar se toman de la configuración de ese agente
              (Configuración → Agentes).
            </p>
            <p className={`mb-2 mt-3 text-xs font-medium ${tc.textSecondary}`}>
              Avanzado (opcional — sobrescribe los valores por defecto del servidor)
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="ElevenLabs Agent ID" tc={tc}>
                <input className={inputCls} value={form.config?.agentId ?? ""} onChange={(e) => setCfg("agentId", e.target.value)} placeholder="agent_..." />
              </Field>
              <Field label="ElevenLabs Phone Number ID" tc={tc}>
                <input className={inputCls} value={form.config?.agentPhoneNumberId ?? ""} onChange={(e) => setCfg("agentPhoneNumberId", e.target.value)} placeholder="phnum_..." />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Objetivo / instrucciones extra" tc={tc}>
                <textarea className={`${inputCls} min-h-[64px]`} value={form.config?.objective ?? ""} onChange={(e) => setCfg("objective", e.target.value)} placeholder="Confirmar correo y entender la urgencia del proyecto." />
              </Field>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled !== false} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            <span className={tc.text}>Activado</span>
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text}`}>Cancelar</button>
            <button onClick={save} disabled={saving} className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400 disabled:opacity-60">
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      }
    />
  );
}

// --- lead picker (test call) -----------------------------------------------
function LeadPickerModal({
  tc,
  workflow,
  onClose,
}: {
  tc: ReturnType<typeof buildThemeClasses>;
  workflow: Workflow;
  onClose: () => void;
}) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [callingId, setCallingId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const page = await fetchLeads({ limit: 50 });
        // Any lead we can dial — `whatsapp` OR the plain `phone` a landing lead
        // gets when it unchecks "este número tiene WhatsApp".
        setLeads(page.data.filter(isLeadCallable));
      } catch {
        setLeads([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const call = async (lead: Lead) => {
    setCallingId(lead.id);
    try {
      const placed = await placeAiCall(lead.id, workflow.id);
      // 201 does NOT mean the provider accepted the call (it returns the failed
      // record too) — warn with the real reason instead of a green toast.
      if (!announceCallProblem(placed)) {
        Swal.fire({
          icon: "success",
          title: "Llamada iniciada",
          text: `Se está llamando a ${lead.name ?? leadPhone(lead)}. La grabación y transcripción aparecerán en Tareas → Llamadas.`,
          confirmButtonColor: brand.primary,
        });
      }
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({ icon: "error", title: "No se pudo llamar", text: err.response?.data?.message || err.message, confirmButtonColor: brand.primary });
    } finally {
      setCallingId(null);
    }
  };

  return (
    <Modal
      title="Probar llamada con un lead"
      onClose={onClose}
      themeClasses={tc}
      content={
        <div className="space-y-3">
          <p className={`text-xs ${tc.textSecondary}`}>Selecciona un lead con teléfono para colocar una llamada de IA ahora.</p>
          {loading ? (
            <p className={`text-sm ${tc.textSecondary}`}><i className="ri-loader-4-line mr-2 animate-spin" />Cargando leads...</p>
          ) : leads.length === 0 ? (
            <p className={`text-sm ${tc.textSecondary}`}>No hay leads con teléfono. Crea un lead con WhatsApp para probar.</p>
          ) : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {leads.map((l) => (
                <div key={l.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${tc.border}`}>
                  <div>
                    <p className={`text-sm font-medium ${tc.text}`}>{l.name ?? "—"}</p>
                    <p className={`text-xs ${tc.textSecondary}`}>{leadPhone(l)}{l.company ? ` · ${l.company}` : ""}</p>
                  </div>
                  <button
                    onClick={() => call(l)}
                    disabled={callingId === l.id}
                    className="rounded-lg bg-teal-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-400 disabled:opacity-60"
                  >
                    {callingId === l.id ? "Llamando..." : <><i className="ri-phone-line mr-1" />Llamar</>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      }
    />
  );
}

// --- small UI helpers ------------------------------------------------------
function Switch({ on, busy, onClick }: { on: boolean; busy: boolean; onClick: () => void }) {
  return (
    <UiSwitch
      checked={on}
      onChange={onClick}
      disabled={busy}
      title={on ? "Activado" : "Desactivado"}
    />
  );
}

function Stat({ label, value, tc }: { label: string; value: number; tc: ReturnType<typeof buildThemeClasses> }) {
  return (
    <div className={`rounded-lg border py-2 ${tc.border}`}>
      <p className={`text-base font-bold ${tc.text}`}>{value}</p>
      <p className={`text-[10px] ${tc.textSecondary}`}>{label}</p>
    </div>
  );
}

function Field({ label, tc, children }: { label: string; tc: ReturnType<typeof buildThemeClasses>; children: React.ReactNode }) {
  return (
    <div>
      <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{label}</label>
      {children}
    </div>
  );
}
